class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    this.button = this.querySelector('button');
    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }
  
  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    const index = this.dataset.index || this.getAttribute('data-index');
    if (cartItems && index) {
      cartItems.updateQuantity(index, 0);
    }
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    // Only listen for change events - quantity buttons are handled by QuantityInput
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList.contains('quantity__input')) return;
    const index = input.dataset.index || input.getAttribute('data-index');
    const quantity = input.value;
    const name = input.getAttribute('name');
    if (index && quantity !== undefined) {
      this.updateQuantity(index, quantity, name);
    }
  }

  getSectionsToRender() {
    let sections = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset?.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset?.id,
        selector: '.js-content',
      }
    ];
    if (document.querySelector('#main-cart-footer .free-shipping')) {
      sections.push({
        id: 'main-cart-progress',
        section: document.getElementById('main-cart-progress')?.dataset?.id,
        selector: '.js-contents',
      });
    }
    if (document.querySelector('#primary-cart-item .free-shipping')) {
      sections.push({
        id: 'main-cart-progress',
        section: document.getElementById('main-cart-progress')?.dataset?.id,
        selector: '#primary-cart-item',
      });
    }
    return sections.filter(s => s.section);
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${window.routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;
          const elementToReplace = sectionEl.querySelector(section.selector) || sectionEl;
          if (elementToReplace && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }
        }));

        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        this.disableLoading();
      }).catch((e) => {
        console.error('Cart update error:', e);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings?.error || 'An error occurred';
        }
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      if (lineItemError && quantityElement) {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = window.cartStrings?.quantityError?.replace('[quantity]', quantityElement.value) || '';
      }
    }
    this.currentItemCount = itemCount;
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }
    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => { cartStatus.setAttribute('aria-hidden', true); }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement?.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${window.routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
      }, 300))
    }
  });
}

// Initialize cart drawer remove buttons only
document.addEventListener('DOMContentLoaded', initCartDrawerRemoveButtons);
document.addEventListener('shopify:section:load', initCartDrawerRemoveButtons);

function initCartDrawerRemoveButtons() {
  // Handle remove buttons in cart drawer only
  document.querySelectorAll('cart-drawer cart-remove-button, cart-drawer-items cart-remove-button').forEach((removeBtn) => {
    if (removeBtn.dataset.cartBound) return;
    removeBtn.dataset.cartBound = 'true';
    const button = removeBtn.querySelector('button');
    if (button) {
      button.addEventListener('click', function(event) {
        event.preventDefault();
        event.stopPropagation();
        const cartItems = removeBtn.closest('cart-items') || removeBtn.closest('cart-drawer-items');
        const index = removeBtn.dataset.index || removeBtn.getAttribute('data-index');
        if (cartItems && index) {
          cartItems.updateQuantity(index, 0);
        }
      });
    }
  });
}

// Re-initialize after cart drawer content updates
const cartDrawerObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      setTimeout(initCartDrawerRemoveButtons, 100);
    }
  });
});

const cartDrawer = document.querySelector('cart-drawer');
if (cartDrawer) {
  cartDrawerObserver.observe(cartDrawer, { childList: true, subtree: true });
}

// Fetch all the details element.
document.addEventListener("DOMContentLoaded", () => {
  const detailsElement = document.querySelector(".cart-group");
  if (!detailsElement) return;
  const details = detailsElement.querySelectorAll("details");
  details.forEach((targetDetail) => {
    targetDetail.addEventListener("click", () => {
      details.forEach((detail) => {
        if (detail !== targetDetail) {
          detail.removeAttribute("open");
        }
      });
    });
  });
});
